/* Состояние экрана «Говорящий кружок» поверх общей формы (use-studio-form): кто говорит, чем говорит, голос, формат,
   кадр и фрагмент, данные персонажа и пресеты звука. Все записи — только в опубликованные ключи (circle-model.circleKeys);
   смена источника очищает противоположные входы и загрузки до создания заявки (frozen intent не меняется: при locked — ничего). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { endpoints } from '../../../api';
import type { UserAvatar } from '../../../api/people';
import type { Voice } from '../../../api/types';
import { uiFormat, useAccountLocale } from '../../../i18n';
import { usePeopleData, type PeopleData } from '../../shoot/kit/use-people-data';
import { useStudioDocument } from '../use-studio-document';
import { artworkUrl } from '../studio-art-model';
import type { CircleFormState } from './CircleFieldInput';
import type { CircleMedia } from './CircleUpload';
import type { CircleVoiceConfig, CircleVoiceRow } from './CircleSettingsCard';
import { mmss } from './order-tail-model';
import { toolField } from './tool-form';
import { isSkinKey, useCircleSkinArt } from './use-circle-skin-art';
import {
  CIRCLE_OCCASIONS,
  circleAudioRevoice,
  circleCharacterId,
  circleCropValue,
  circleCropWrite,
  circleFace,
  circleFormat,
  circleFormatChoices,
  circleGroup,
  circleKeys,
  circleLoopOn,
  circleMissing,
  circleMotionOn,
  circleMotionValue,
  circleMotionWrite,
  circleNewWords,
  circleOccasions,
  circleOwnAllowed,
  circlePlaceholder,
  circlePolicy,
  circleResetKeys,
  circleSfxOn,
  circleSources,
  circleSpeech,
  circleTextBounds,
  circleTitle,
  circleTrimValue,
  circleTrimWrite,
  circleVizChoices,
  circleWho,
  type CirclePreset,
  type CircleSpeech,
  type CircleWho,
} from './circle-model';

export interface CircleScreenOptions {
  preset?: CirclePreset;
  /** Цена за единицу из app.price (только сервер) */
  unit?: number | null;
  /** Вход voicePick: лист сразу на секции «Голос» */
  voicePick?: boolean;
  /** Кто говорит со входа (`who` входа студии) */
  entryWho?: CircleWho | null;
  /** Формат со входа (`fmt`) */
  entryFormat?: string | null;
  onSpeechChoice?(value: string | undefined): void;
}
type Inputs = Record<string, string>;
/** «Вы» в ряду: выбранный вариант или свои фото (как у ряда лиц BotPeople). */
function selfFace(data: PeopleData, avatar: UserAvatar | null) {
  const variant = data.self?.src === 'avatar' ? (data.variants.find(item => String(item.id) === String(data.self?.pick)) ?? avatar) : null;
  return {
    name: 'Вы',
    sub: variant ? variant.variant_name : undefined,
    image: variant?.image_url ?? data.self?.photos[0]?.url ?? null,
    ready: !!data.self?.ready || !!variant,
  };
}
const drop = (inputs: Inputs, keys: Array<string | undefined>) => {
  const next = { ...inputs };
  for (const key of keys) if (key) delete next[key];
  return next;
};

export function useCircleScreen(state: CircleFormState, avatar: UserAvatar | null, options: CircleScreenOptions = {}) {
  const { form, inputs, locked } = state;
  const policy = circlePolicy(form);
  const keys = useMemo(() => circleKeys(form), [form]);
  const sources = useMemo(() => circleSources(form), [form]);
  const preset = options.preset ?? {};
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const people = usePeopleData(sources.char);
  const [pick, setPick] = useState<CircleWho | null>(() => {
    if (options.entryWho && ['photo', 'video', 'audio'].includes(options.entryWho)) return options.entryWho;
    if (preset.audio) return 'audio';
    const s = circleSources(form);
    return s.photo && !s.char && !s.me && !s.person && !s.video ? 'photo' : null;
  });
  const [speechChoice, setSpeechChoice] = useState<CircleSpeech | undefined>();
  const [cropping, setCropping] = useState<CircleMedia | null>(null);
  const [media, setMedia] = useState<{ photo?: CircleMedia | null; video?: CircleMedia | null }>({});
  const [audioMs, setAudioMs] = useState(0);
  const [audioVoice, setAudioVoice] = useState<Voice | null>(null);
  const who = circleWho(form, inputs, pick);
  const group = circleGroup(who);
  const speech = circleSpeech(form, inputs, who, speechChoice);
  const format = preset.template ? 'circle' : circleFormat(form, inputs);
  const round = format === 'circle';
  const face = circleFace(form, inputs);
  const characterId = circleCharacterId(form, inputs);
  const details = useStudioDocument(
    signal => (characterId ? endpoints.avatarCatalog.details(characterId, locale, signal) : Promise.resolve(null)),
    `circle-character:${characterId ?? 0}:${locale}`,
  );
  const character = characterId ? (details.data?.avatar ?? null) : null;
  const sfxOn = circleSfxOn(form, who, speech);
  const sfxPresets = useStudioDocument(
    signal => (sfxOn ? endpoints.avatarCatalog.sfx(characterId ?? undefined, locale, signal) : Promise.resolve(null)),
    `circle-sfx:${sfxOn ? 1 : 0}:${characterId ?? 0}:${locale}`,
  );
  const set = (mutate: (current: Inputs) => Inputs) => {
    if (locked) return;
    state.setInputs(current => mutate(current));
  };
  const clearUploads = (list: Array<string | undefined>) => {
    for (const key of list) if (key && state.uploadIds[key]) state.clearUpload(key);
  };

  /* ── Зеркала контракта экрана: who, speech, voice_mode — только если форма их публикует ── */
  const voiceMode = speech === 'text' ? (inputs[keys.textVoice ?? ''] ? 'voice' : '') : inputs[keys.audioVoice ?? ''] ? 'voice' : 'src';
  useEffect(() => {
    if (locked || !form) return;
    state.setInputs(current => {
      const next = { ...current };
      if (keys.who) {
        if (who) next[keys.who] = who;
        else delete next[keys.who];
      }
      if (keys.speech) {
        if (who && who !== 'audio') next[keys.speech] = speech;
        else delete next[keys.speech];
      }
      if (keys.voiceMode && !next[keys.voiceMode] && voiceMode) next[keys.voiceMode] = voiceMode;
      return Object.keys(next).length === Object.keys(current).length && Object.entries(next).every(([key, value]) => current[key] === value) ? current : next;
    });
    // Зеркала пересчитываются по смене выбора; запись идемпотентна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, speech, voiceMode, locked, form, keys]);
  /* ── Вход: «Кружок из аудио», формат входа, шаблон закрепляет кружок ── */
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !form || locked) return;
    applied.current = true;
    state.setInputs(current => {
      let next = { ...current };
      if ((preset.audio || options.entryWho === 'audio') && sources.audio) {
        if (keys.source) next[keys.source] = 'audio';
        const viz = circleVizChoices(form)[0];
        if (keys.viz && viz && !next[keys.viz]) next[keys.viz] = viz[0];
      } else if (options.entryWho === 'video' && sources.video && keys.source) next[keys.source] = 'video';
      const formats = circleFormatChoices(form, options.entryFormat ?? '').map(item => item[0]);
      if (keys.format && preset.template && formats.includes('circle')) next[keys.format] = 'circle';
      else if (keys.format && options.entryFormat && formats.includes(options.entryFormat)) next[keys.format] = options.entryFormat;
      if (preset.audio && keys.audio && !keys.text) next = drop(next, [keys.text]);
      return next;
    });
    // Вход применяется один раз, когда форма пришла.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, locked]);
  /* ── Голос персонажа по умолчанию (defVoice): текст без голоса — первый голос персонажа ── */
  useEffect(() => {
    if (locked || !character || speech !== 'text' || !keys.textVoice || inputs[keys.textVoice]) return;
    const id = character.default_voice_id ?? character.voice_ids[0];
    if (id) set(current => ({ ...current, [keys.textVoice!]: String(id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character?.id, speech, locked]);

  /* ── Кто говорит ── */
  /** Смена группы источника (лицо · своё видео · анимация звука) очищает все входы прежней группы. */
  const switchGroup = (next: 'face' | 'video' | 'audio', extra: (inputs: Inputs) => Inputs = value => value) => {
    const reset = circleResetKeys(keys, policy);
    clearUploads(reset);
    setSpeechChoice(undefined);
    options.onSpeechChoice?.(undefined);
    setCropping(null);
    setAudioMs(0);
    set(current => {
      const next2 = drop(current, reset);
      if (keys.source) next2[keys.source] = next;
      if (keys.who) {
        if (next === 'face') delete next2[keys.who];
        else next2[keys.who] = next;
      }
      if (next === 'audio') {
        const viz = form ? circleVizChoices(form)[0] : undefined;
        if (keys.viz && viz) next2[keys.viz] = viz[0];
      }
      return extra(next2);
    });
  };
  const faceKeys = [keys.who, keys.cast, keys.avatar, keys.me, keys.person, keys.photo, keys.skin, keys.crop, keys.cropX, keys.cropY, keys.zoom, ...(policy?.face_fields ?? [])];
  const writeFace = (value: string) => (current: Inputs) => {
    const next = drop(current, faceKeys);
    const [kind, id] = value.split(':');
    if (keys.who) next[keys.who] = kind;
    if (kind === 'char') {
      if (keys.avatar) next[keys.avatar] = id;
      else if (keys.cast) next[keys.cast] = `character:${id}`;
    } else if (kind === 'person') {
      if (keys.person) next[keys.person] = id;
      else if (keys.cast) next[keys.cast] = `person:${id}`;
    } else if (kind === 'me') {
      const variant = avatar?.status === 'ready' ? avatar.id : people.self?.src === 'avatar' && people.self.pick ? Number(people.self.pick) : null;
      if (keys.me) next[keys.me] = JSON.stringify(variant ? { src: 'avatar', avatar_id: variant } : { src: 'photos' });
      else if (keys.cast) next[keys.cast] = variant ? `user_avatar:${variant}` : 'self';
    }
    return next;
  };
  /** Ряд лиц: `me`, `person:<id>`, `char:<id>`, свои круги `own:photo`, `own:video`; повторное касание снимает выбор. */
  const pickFace = (value: string) => {
    if (locked) return;
    const current = who === 'photo' ? 'own:photo' : who === 'video' ? 'own:video' : face;
    if (value === current) {
      if (value === 'own:video') switchGroup('face');
      else {
        clearUploads([keys.photo]);
        set(current2 => drop(current2, faceKeys));
      }
      setPick(null);
      setCropping(null);
      return;
    }
    if (value === 'own:video') {
      setPick('video');
      switchGroup('video');
      return;
    }
    if (group !== 'face') switchGroup('face');
    clearUploads([keys.photo]);
    setCropping(null);
    if (value === 'own:photo') {
      setPick('photo');
      set(current2 => ({ ...drop(current2, faceKeys), ...(keys.who ? { [keys.who]: 'photo' } : {}) }));
      return;
    }
    setPick(null);
    set(writeFace(value));
  };

  /* ── Чем говорит ── */
  const chooseSpeech = (next: CircleSpeech) => {
    if (locked || next === speech) return;
    const distinct = (key: string | undefined, other: string | undefined) => (key && key !== other ? key : undefined);
    const cleared =
      next === 'text'
        ? [keys.audio, distinct(keys.audioVoice, keys.textVoice), ...(policy?.speech_fields ?? []).filter(key => key !== keys.text)]
        : next === 'audio'
          ? [keys.text, keys.occasion, distinct(keys.textVoice, keys.audioVoice), ...(policy?.speech_fields ?? []).filter(key => key !== keys.audio)]
          : [keys.text, keys.occasion, keys.audio, distinct(keys.textVoice, keys.audioVoice), keys.loop, ...(policy?.speech_fields ?? [])];
    clearUploads(cleared);
    if (next !== 'audio') setAudioMs(0);
    if (keys.voiceMode && keys.textVoice === keys.audioVoice) cleared.push(keys.textVoice, keys.voiceMode);
    set(current => drop(current, cleared));
    setSpeechChoice(next);
    options.onSpeechChoice?.(next === 'own' ? 'original' : next);
  };
  const newWords = circleNewWords(form);
  const speechChoices: CircleSpeech[] =
    who === 'video'
      ? [...(circleOwnAllowed(form) ? (['own'] as CircleSpeech[]) : []), ...newWords]
      : who === 'audio'
        ? ([keys.audio ? 'audio' : null, keys.text ? 'text' : null].filter(Boolean) as CircleSpeech[])
        : ([keys.text ? 'text' : null, keys.audio ? 'audio' : null].filter(Boolean) as CircleSpeech[]);

  /* ── Голос (§5.2) ── */
  const revoiceAudio = !!keys.audioVoice && (policy ? circleAudioRevoice(policy) : true);
  const revoiceOwn = !!keys.audioVoice && policy?.video_revoice === true;
  const charVoiceId = character ? (character.default_voice_id ?? character.voice_ids[0] ?? null) : null;
  const charVoice = details.data?.voices.find(item => item.id === charVoiceId) ?? null;
  const charImage = character ? (people.characters.find(item => item.id === character.id)?.image ?? null) : null;
  const voice: CircleVoiceConfig | null = (() => {
    if (!who || (who === 'audio' && speech === 'audio')) return null;
    if (speech === 'text') {
      if (!keys.textVoice) return null;
      const rows: CircleVoiceRow[] = [];
      if (who === 'video' && circleOwnAllowed(form)) rows.push({ value: 'keep', label: 'Оставить мою речь', sub: 'Слова и голос как в видео', short: 'Как в видео' });
      if (charVoiceId && charVoice?.can_tts !== false)
        rows.push({ value: `voice:${charVoiceId}`, label: charVoice?.name ?? 'Голос персонажа', sub: 'Голос персонажа', image: charImage });
      const id = inputs[keys.textVoice];
      const other = id && !rows.some(row => row.value === `voice:${id}`) ? (state.voice && String(state.voice.id) === id ? state.voice : ({ id: Number(id), name: 'Голос из каталога' } as Voice)) : null;
      return {
        rows,
        current: id ? `voice:${id}` : '',
        s2s: false,
        other,
        image: id && id === String(charVoiceId) ? charImage : null,
        onPick: value => {
          if (value === 'keep') return chooseSpeech('own');
          const voiceId = value.slice(6);
          set(current => ({ ...current, [keys.textVoice!]: voiceId, ...(keys.voiceMode ? { [keys.voiceMode]: voiceId === String(charVoiceId) ? 'char' : 'voice' } : {}) }));
        },
        onOther: picked => {
          state.setVoice(picked);
          set(current => ({ ...current, [keys.textVoice!]: String(picked.id), ...(keys.voiceMode ? { [keys.voiceMode]: 'voice' } : {}) }));
        },
      };
    }
    const own = speech === 'own';
    if (own ? false : !revoiceAudio) return null;
    const rows: CircleVoiceRow[] = own
      ? [{ value: 'src', label: 'Оставить мою речь', sub: 'Слова и голос как в видео', short: 'Как в видео' }]
      : [{ value: 'src', label: 'Как в записи', sub: 'Без изменений', short: 'Как в записи' }];
    if (!own && charVoiceId && charVoice?.can_s2s) rows.push({ value: `voice:${charVoiceId}`, label: 'Голос персонажа', sub: charVoice.name, short: charVoice.name, image: charImage });
    const id = keys.audioVoice ? inputs[keys.audioVoice] : '';
    const other = id && !rows.some(row => row.value === `voice:${id}`) ? (audioVoice && String(audioVoice.id) === id ? audioVoice : ({ id: Number(id), name: 'Голос из каталога' } as Voice)) : null;
    const canChange = own ? revoiceOwn : revoiceAudio;
    return {
      rows,
      current: id ? `voice:${id}` : 'src',
      s2s: true,
      other: canChange ? other : null,
      image: id && id === String(charVoiceId) ? charImage : null,
      onPick: value => {
        if (value === 'src') set(current => ({ ...drop(current, [keys.audioVoice]), ...(keys.voiceMode ? { [keys.voiceMode]: 'src' } : {}) }));
        else if (canChange) set(current => ({ ...current, [keys.audioVoice!]: value.slice(6), ...(keys.voiceMode ? { [keys.voiceMode]: 'char' } : {}) }));
      },
      onOther: picked => {
        if (!canChange) return;
        setAudioVoice(picked);
        set(current => ({ ...current, [keys.audioVoice!]: String(picked.id), ...(keys.voiceMode ? { [keys.voiceMode]: 'voice' } : {}) }));
      },
    };
  })();
  const voiceCanChange = speech === 'own' ? revoiceOwn : true;

  /* ── Формат, вид, движение, звук, «Зациклить» ── */
  const formatChoices = circleFormatChoices(form, format);
  const setFormat = (value: string) => keys.format && !preset.template && set(current => ({ ...current, [keys.format!]: value }));
  /* Ключи live-avatar/… видов — не адреса: картинки идут через BFF в общий кэш (use-circle-skin-art) */
  const skinArt = useCircleSkinArt(character, who === 'char' && !!keys.skin);
  const skins =
    who === 'char' && keys.skin && character?.skins.length
      ? [
          { id: '', name: 'Обычный', image: charImage },
          ...character.skins.map(skin => ({ id: String(skin.id), name: skin.name, image: skinArt[skin.id] ?? (isSkinKey(skin.image_url) ? null : skin.image_url) })),
        ]
      : [];
  const motionOn = circleMotionOn(form, who);
  const motionField = toolField(form, keys.motion ?? keys.movement ?? '');
  const motion = circleMotionValue(form, inputs);
  const sfxField = keys.sfx ? toolField(form, keys.sfx) : undefined;
  const sfxMax = Math.min(policy?.sfx_max_length ?? 120, sfxField?.max_length ?? 120);
  const loopOn = circleLoopOn(form, who, speech);

  /* ── Кадр и фрагмент ── */
  const crop = circleCropValue(form, inputs);
  const trim = circleTrimValue(form, inputs);
  const maxMs = policy?.max_duration_ms ?? 60000;
  const videoMedia = media.video ?? null;
  const onMedia = (kind: 'photo' | 'video', value: CircleMedia | null) => {
    setMedia(current => (current[kind] === value ? current : { ...current, [kind]: value }));
    if (kind === 'video' && value && value.durationMs > 0 && !trim && (keys.trim || keys.start) && !locked)
      state.setInputs(current => circleTrimWrite(form, current, { start: 0, end: Math.min(value.durationMs, maxMs) }));
  };
  const trimWanted = who === 'video' && !!videoMedia?.durationMs && !cropping && (speech === 'own' || videoMedia.durationMs > maxMs) && (!!keys.trim || !!keys.start);
  const ownMs = videoMedia ? (trim ? trim.end - trim.start : Math.min(videoMedia.durationMs, maxMs)) : 0;

  /* ── Текст ── */
  const bounds = circleTextBounds(form, inputs);
  const occasions = circleOccasions(form);

  /* ── Кто говорит: картинка, имя, подпись ── */
  const me = selfFace(people, avatar);
  const person = face.startsWith('person:') ? people.persons.find(item => `person:${item.id}` === face) ?? null : null;
  const skinPicked = skins.find(item => item.id === (keys.skin ? inputs[keys.skin] ?? '' : ''));
  const vizChoices = circleVizChoices(form);
  const vizValue = keys.viz ? inputs[keys.viz] || vizChoices[0]?.[0] || '' : '';
  const viz = vizChoices.find(item => item[0] === vizValue);
  const heroLine: { name: string; sub: string; image: string | null } =
    who === 'char'
      ? { name: character?.full_name || character?.name || people.characters.find(item => item.id === characterId)?.name || '', sub: character?.source ?? '', image: artworkUrl(skinPicked?.image) ? skinPicked!.image : charImage }
      : who === 'me'
        ? { name: me.sub ? `${me.name} · ${me.sub}` : me.name, sub: me.sub ? 'Ваш вариант' : 'Ваши фото', image: me.image ?? null }
        : who === 'person'
          ? { name: person?.name ?? '', sub: person?.rel ?? '', image: person?.photos[0]?.url ?? null }
          : who === 'audio'
            ? { name: viz?.[1] ?? '', sub: viz?.[2] ?? '', image: null }
            : who
              ? { name: '', sub: '', image: null }
              : { name: 'Кто говорит?', sub: 'Выберите в ряду ниже', image: null };
  const speakerName = who === 'char' ? (people.characters.find(item => item.id === characterId)?.name ?? character?.name ?? '') : who === 'person' ? (person?.name ?? '') : '';

  /* ── Чего не хватает, заметка, название ── */
  const missing = form
    ? circleMissing(form, inputs, state.rights, speechChoice === 'own' ? 'original' : speechChoice, {
        pick,
        cropping: !!cropping,
        personWithoutPhoto: who === 'person' && person && !person.photos.length ? person.name : null,
      })
    : null;
  const note: { kind: 'mute' | 'warn'; text: string } | null =
    who === 'video' && speech !== 'own' && policy?.video_auto_loop === true
      ? { kind: 'mute', text: 'Если речь длиннее видео, ролик повторится.' }
      : speech === 'audio' && audioMs > maxMs
        ? { kind: 'mute', text: uiFormat('Войдёт первая минута из {0}', mmss(audioMs / 1000)) }
        : null;
  const title = circleTitle(preset, format, who);

  return {
    form,
    preset,
    keys,
    policy,
    sources,
    people,
    who,
    group,
    pick,
    face,
    speech,
    speechChoices,
    newWords,
    format,
    round,
    title,
    missing,
    talking: !!who && !missing,
    note,
    hero: heroLine,
    speakerName,
    placeholder: circlePlaceholder(who, speakerName),
    bounds,
    occasions,
    voice,
    voiceCanChange,
    formatChoices,
    setFormat,
    skins,
    skin: keys.skin ? inputs[keys.skin] ?? '' : '',
    setSkin: (value: string) => keys.skin && set(current => (value ? { ...current, [keys.skin!]: value } : drop(current, [keys.skin]))),
    motionOn,
    motion,
    motionMax: motionField?.max_length ?? 200,
    setMotion: (value: { do: string; not: string }) => set(current => circleMotionWrite(form, current, value)),
    sfxOn,
    sfx: keys.sfx ? inputs[keys.sfx] ?? '' : '',
    sfxMax,
    sfxPresets: (sfxPresets.data?.items ?? []).map(item => [item.label, Array.from(item.prompt).slice(0, sfxMax).join('')] as [string, string]),
    setSfx: (value: string) => keys.sfx && set(current => (value ? { ...current, [keys.sfx!]: value } : drop(current, [keys.sfx]))),
    loopOn,
    loop: keys.loop ? inputs[keys.loop] === 'true' : false,
    setLoop: (value: boolean) => keys.loop && set(current => ({ ...current, [keys.loop!]: value ? 'true' : 'false' })),
    vizChoices,
    vizValue,
    setViz: (value: string) => keys.viz && set(current => ({ ...current, [keys.viz!]: value })),
    crop,
    cropping,
    startCrop: (value: CircleMedia) => !locked && setCropping(value),
    finishCrop: (value: { cx: number; cy: number; z: number } | null) => {
      if (value) set(current => circleCropWrite(form, current, value));
      setCropping(null);
    },
    trim,
    trimWanted,
    maxMs,
    videoMedia,
    photoMedia: media.photo ?? null,
    ownMs,
    setTrim: (value: { start: number; end: number }) => set(current => circleTrimWrite(form, current, value)),
    onMedia,
    audioMs,
    setAudioMs,
    unit: options.unit ?? null,
    pickFace,
    chooseSpeech,
    setText: (value: string) =>
      keys.text &&
      set(current => {
        const next = { ...current, [keys.text!]: value };
        const chosen = keys.occasion ? CIRCLE_OCCASIONS.find(item => item[0] === current[keys.occasion!]) : undefined;
        return chosen && chosen[1] !== value.trim() ? drop(next, [keys.occasion]) : next;
      }),
    text: keys.text ? inputs[keys.text] ?? '' : '',
    occasion: keys.occasion ? inputs[keys.occasion] ?? '' : '',
    pickOccasion: (label: string, text: string) =>
      set(current => {
        const on = keys.occasion && current[keys.occasion] === label && (current[keys.text ?? ''] ?? '').trim() === text;
        if (on) return drop(current, [keys.occasion, keys.text]);
        return { ...current, ...(keys.text ? { [keys.text]: text } : {}), ...(keys.occasion ? { [keys.occasion]: label } : {}) };
      }),
    me,
    person,
    characterImage: charImage,
    character,
    voicePick: !!options.voicePick,
  };
}
export type CircleScreen = ReturnType<typeof useCircleScreen>;
